import { BodyText } from '@block-explorer/ui';
import React from 'react';
import {
  CaptionText,
  DEFAULT_AMOUNT_PRECISION,
  DEFAULT_PRECISION,
  FlexColumn,
  FlexRow,
} from '@make-software/cspr-design';
import {
  formatTokenAmount,
  ftTokenAmountToCurrency,
  getAmountFromMotes,
} from '../../utils/ft-token-amount';
import { currencyPrecisionByCase, PrecisionCase } from '../../utils/currency';
import FiatCurrency from '../multi-currency/multi-currency';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { buildRouterPath } from '../../router/utils';
import { CONTRACT_PACKAGE_PATH } from '../../router/paths';
import TooltipWithExtendedInfo from '../tooltip-with-extended-info/tooltip-with-extended-info';
import { HistoryLink } from '../history-link/history-link';
import {
  DeployContractPackageResult,
  FTAmountDataType,
  FtTokenMarketData,
} from '@block-explorer/entities';

interface FtTokenAmountWithSymbolProps {
  hash: string;
  symbol: string;
  fullContractName: string;
  formattedCep18Amount: string;
}
interface FtTokenSymbolWithLinkProps {
  amount: string;
  hash: string;
  fullContractName: string;
  cep18AmountData: Omit<FTAmountDataType, 'totalSupply' | 'totalSupplyMotes'> &
    FtTokenMarketData;
  precisionCase?: PrecisionCase;
}
interface TableFormatFtAmountWithSymbolProps {
  amount: string | undefined | null;
  loading: boolean;
  contractPackageData?: DeployContractPackageResult;
  outgoingAmount?: boolean;
  cep18AmountData: Omit<FTAmountDataType, 'totalSupply' | 'totalSupplyMotes'> &
    FtTokenMarketData;
  precisionCase?: PrecisionCase;
}

const FtTokenSymbolWithLink = ({
  hash,
  symbol,
  fullContractName,
  formattedCep18Amount,
}: FtTokenAmountWithSymbolProps) => {
  const { t } = useTranslation();

  const cep18SymbolLink = buildRouterPath(
    CONTRACT_PACKAGE_PATH,
    { id: hash },
    undefined
  );

  return (
    <TooltipWithExtendedInfo
      hash={hash}
      tooltipCaption={t('Contract Package Hash')}
      extendedLine={{
        title: fullContractName,
        caption: t('Contract name'),
      }}
    >
      <FlexRow gap={4} justify={'end'} align={'center'}>
        {formattedCep18Amount}
        <HistoryLink href={cep18SymbolLink}>{symbol}</HistoryLink>
      </FlexRow>
    </TooltipWithExtendedInfo>
  );
};

const FtTokenAmountWithSymbol = ({
  amount,
  hash,
  fullContractName,
  cep18AmountData,
  precisionCase,
}: FtTokenSymbolWithLinkProps) => {
  const calculatedCep18Amount =
    cep18AmountData?.decimals != null && amount
      ? getAmountFromMotes(amount, cep18AmountData.decimals)
      : null;

  const formattedCep18Amount = formatTokenAmount(
    calculatedCep18Amount,
    precisionCase ? currencyPrecisionByCase(precisionCase) : DEFAULT_PRECISION,
    true
  );

  const currencyAmount =
    calculatedCep18Amount && cep18AmountData.price
      ? ftTokenAmountToCurrency(calculatedCep18Amount, cep18AmountData.price)
      : null;

  return (
    <FlexColumn itemsSpacing={4}>
      <FtTokenSymbolWithLink
        hash={hash}
        fullContractName={fullContractName}
        symbol={cep18AmountData.symbol}
        formattedCep18Amount={formattedCep18Amount}
      />
      <CaptionText size={2} variation="darkGray">
        <FiatCurrency
          amount={currencyAmount}
          precision={
            precisionCase
              ? currencyPrecisionByCase(precisionCase)
              : DEFAULT_AMOUNT_PRECISION
          }
          nullLabel={'-'}
        />
      </CaptionText>
    </FlexColumn>
  );
};

const TableFormatFtAmountWithSymbol = ({
  amount,
  outgoingAmount,
  cep18AmountData,
  contractPackageData,
  precisionCase,
  loading,
}: TableFormatFtAmountWithSymbolProps) => {
  if (loading || !contractPackageData) {
    return (
      <BodyText size={3}>
        <Skeleton />
      </BodyText>
    );
  }

  return (
    <BodyText size={3} monotype noWrap>
      {amount == null ? (
        '-'
      ) : (
        <FlexRow gap={0} justify={'end'}>
          {outgoingAmount && (
            <BodyText size={3} monotype>
              -
            </BodyText>
          )}
          <FtTokenAmountWithSymbol
            hash={contractPackageData.contract_package_hash}
            fullContractName={contractPackageData.name || ''}
            amount={amount}
            cep18AmountData={cep18AmountData}
            precisionCase={precisionCase}
          />
        </FlexRow>
      )}
    </BodyText>
  );
};

export default TableFormatFtAmountWithSymbol;
